import React, { useState, useEffect } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import PropTypes from "prop-types";
import API_BASE_URL from "../../../../Constants";

import { testDetailsFields } from "./config"; // Ensure this is imported
import RenderFields from "../../../../components/Modals/ModalFields/RenderFields";

const AgencyTestTable = ({ agency, isEditMode }) => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTest, setSelectedTest] = useState(null); // Track the selected test
  const [showTestDetails, setShowTestDetails] = useState(false); // Control visibility of test details

  // Fetch tests for the agency
  useEffect(() => {
    const fetchTests = async () => {
      if (!agency || !agency.agencyId) {
        setTests([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await fetch(
          `${API_BASE_URL}/api/inspections/agency/agencyTestsForAgency/${agency.agencyId}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          }
        );
        if (!response.ok) {
          throw new Error(
            `Failed to fetch agency tests: ${response.statusText}`
          );
        }
        const data = await response.json();
        setTests(data);
      } catch (err) {
        console.error(err.message);
        setTests([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTests();
  }, [agency]);

  // Handle test row click
  const handleTestClick = (test) => {
    setSelectedTest(test);
    setShowTestDetails(true); // Show the test details
  };

  // Handle save in the inline card
  const handleSaveTest = async (updatedTest) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/inspections/agency/agencyTests/${updatedTest.testId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(updatedTest),
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to update test: ${response.statusText}`);
      }
      const updatedTests = tests.map((t) =>
        t.testId === updatedTest.testId ? updatedTest : t
      );
      setTests(updatedTests);
    } catch (err) {
      console.error(err.message);
    }
  };

  // Handle test deletion
  const deleteTest = async (testId) => {
    try {
      if (!window.confirm("Are you sure you want to delete this test?")) {
        return;
      }
      const response = await fetch(
        `${API_BASE_URL}/api/inspections/agency/deleteAgencyTest/${testId}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to delete test: ${response.statusText}`);
      }
      setTests(tests.filter((t) => t.testId !== testId));
    } catch (err) {
      console.error(err.message);
    }
  };

  return (
    <div className="p-6 bg-white rounded shadow-md">
      {loading ? (
        <div className="text-blue-500">Loading tests...</div>
      ) : tests.length === 0 ? (
        <div className="text-gray-500">No tests available for this agency.</div>
      ) : (
        <div className="overflow-x-auto">
          {/* Test Table */}
          <table className="table-auto w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                {isEditMode && (
                  <th className="border border-gray-300 px-4 py-2 text-left"></th>
                )}
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Test ID
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Label
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Description
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Frequency
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Sequence
                </th>
                {isEditMode && (
                  <th className="border border-gray-300 px-4 py-2 text-left"></th>
                )}
              </tr>
            </thead>
            <tbody>
              {tests.map((test) => (
                <tr
                  key={test.testId}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleTestClick(test)}
                >
                  {/* Render test details */}
                  <td className="border border-gray-300 px-4 py-2">
                    {test.testId || "N/A"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {test.label || "N/A"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {test.description || "N/A"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {test.frequency || "N/A"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {test.sequence || "N/A"}
                  </td>
                  {isEditMode && (
                    <td className="p-2 border text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click from triggering
                          deleteTest(test.testId);
                        }}
                        className="hover:bg-red-500 rounded-xl transition ease-in-out border-2 p-2 border-gray-300"
                      >
                        <DeleteIcon />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Test Details Card (Inline) */}
          {showTestDetails && selectedTest && (
            <div className="mt-4">
              <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
                <h2 className="text-xl font-bold mb-4">Test Details</h2>
                <RenderFields
                  fields={testDetailsFields} // Ensure this is imported or defined
                  formData={selectedTest}
                  handleChange={(e) => {
                    const { name, value } = e.target;
                    setSelectedTest((prev) => ({ ...prev, [name]: value }));
                  }}
                  readOnly={!isEditMode}
                />
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => {
                      setShowTestDetails(false); // Hide the test details
                      setSelectedTest(null); // Clear the selected test
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-800 font-bold rounded-md hover:bg-gray-400 transition"
                  >
                    Close
                  </button>
                  {isEditMode && (
                    <button
                      onClick={() => {
                        handleSaveTest(selectedTest); // Save the updated test
                      }}
                      className="px-6 py-2 bg-yellow-500 text-gray-800 font-bold rounded-md hover:bg-yellow-600 transition ml-2"
                    >
                      Save
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

AgencyTestTable.propTypes = {
  agency: PropTypes.shape({
    agencyId: PropTypes.number.isRequired,
  }).isRequired,
  isEditMode: PropTypes.bool,
};

export default AgencyTestTable;
